import { FC } from 'react';
// 组件
import SearchForm from '../../../SearchForm';
// 方法
import { filterEmptyValue } from '../../../utils/utils';
import { renderEnumNames } from '../../../utils/renderUtils';
import { useLayoutContext } from '../../../PageLayout/context/LayoutContext';
// 常量
import { CARD_FORM_NAME, CardFormItemProps } from '../../interface';
import { DEFAULT_FIELD_NAMES } from '../../../SuperSelect/interface';
import { SuperFormItemProps } from '../../../SuperForm/interface';
import { TABLE_COLUMN_EMPTY } from '../../../constants/constant';

/** 表单 */
const CardFormItem: FC<CardFormItemProps> = props => {
  const {
    formItemType, name, value, groupKey, groupValue, disabled, enumCode, inputProps, switchProps, radioGroupProps,
    hide, inputNumberProps,checkboxProps, checkboxGroupProps, superSelectProps, superSelectTableProps,
    associationNames, form, required, rules, superTransferProps, selectTabProps,
    changeFormatProps, onChange, ...restProps
  } = props;
  /** 表单值修改回调 */
  const handleChange = (newValue: any, options?: any) => {
    onChange(name, newValue, associationNames, options);
  };
  /** 表单 */
  const renderForm = () => {
    const { enumData } = useLayoutContext();
    const newGroupValue = {...groupValue, ...(form.getFieldValue([CARD_FORM_NAME, groupKey]))};
    const baseProps: any = {
      disabled,
      ...(changeFormatProps ? changeFormatProps(newGroupValue) : undefined)
    };
    const formName = [CARD_FORM_NAME, groupKey, name];
    const baseFormProps: Partial<SuperFormItemProps> = { name: formName, };
    if (!rules && required) {
      baseFormProps.rules = [{required: true}];
    }
    if (hide !== undefined) {
      if (hide === true) return null;
      if (typeof hide === 'function') {
        const newHiddle = hide(newGroupValue);
        if (newHiddle) return null;
      }
    }
    const formValue = filterEmptyValue(form.getFieldValue(formName));
    switch (formItemType) {
      case 'Input':
        return (
          <SearchForm.Input
            disabledRender={formValue}
            {...baseFormProps}
            {...restProps}
            inputProps={{
              ...baseProps,
              ...inputProps,
              onChange: event => {
                handleChange(event.target.value);
                inputProps?.onChange?.(event);
              },
            }}
          />
        );
      case 'InputNumber':
        return (
          <SearchForm.InputNumber
            disabledRender={formValue}
            {...baseFormProps}
            {...restProps}
            inputNumberProps={{
              ...baseProps,
              ...inputNumberProps,
              onChange: newValue => {
                handleChange(newValue);
                inputNumberProps?.onChange?.(newValue);
              },
            }}
          />
        );
      case 'Switch':
        return (
          <SearchForm.Switch
            disabledRender={formValue ? switchProps?.checkedChildren || '是' : (formValue === undefined ? TABLE_COLUMN_EMPTY : switchProps?.unCheckedChildren || '否')}
            {...baseFormProps}
            {...restProps}
            switchProps={{
              ...baseProps,
              ...switchProps,
              onChange: (checked, event) => {
                handleChange(checked);
                switchProps?.onChange?.(checked, event);
              },
            }}
          />
        );
      case 'RadioGroup':
        let radioGroupOption = radioGroupProps?.options;
        if (!radioGroupOption && enumCode && enumData) {
          radioGroupOption = enumData[enumCode] ? enumData[enumCode].items || [] : [];
        }
        return (
          <SearchForm.RadioGroup
            disabledRender={() => {
              if (formValue === undefined || formValue === '' || formValue === null) {
                return TABLE_COLUMN_EMPTY;
              }
              if (!radioGroupOption || !radioGroupOption.length) {
                return TABLE_COLUMN_EMPTY;
              }
              return renderEnumNames(formValue, radioGroupOption as any[], '，');
            }}
            {...restProps}
            {...baseFormProps}
            radioGroupProps={{
              ...baseProps,
              ...radioGroupProps,
              options: radioGroupOption,
              onChange: event => {
                handleChange(event.target.value);
                radioGroupProps?.onChange?.(event);
              },
            }}
          />
        );
      case 'Checkbox':
        return (
          <SearchForm.Checkbox
            disabledRender={formValue ? checkboxProps?.checkedChildren || '是' : checkboxProps?.unCheckedChildren || '否'}
            {...baseFormProps}
            {...restProps}
            checkboxProps={{
              ...baseProps,
              ...checkboxProps,
              onChange: event => {
                handleChange(event.target.checked);
                checkboxProps?.onChange?.(event);
              },
            }}
          />
        );
      case 'CheckboxGroup':
        let checkboxGroupOption = checkboxGroupProps?.options;
        if (!checkboxGroupOption && enumCode && enumData) {
          checkboxGroupOption = enumData[enumCode] ? enumData[enumCode].items || [] : [];
        }
        return (
          <SearchForm.CheckboxGroup
            disabledRender={() => {
              if (formValue === undefined || formValue === '' || formValue === null) {
                return TABLE_COLUMN_EMPTY;
              }
              if (!checkboxGroupOption || !checkboxGroupOption.length) {
                return TABLE_COLUMN_EMPTY;
              }
              return renderEnumNames(formValue, checkboxGroupOption as any[], '，');
            }}
            {...restProps}
            {...baseFormProps}
            checkboxGroupProps={{
              ...baseProps,
              ...checkboxGroupProps,
              options: checkboxGroupOption,
              onChange: newValue => {
                handleChange(newValue);
                checkboxGroupProps?.onChange?.(newValue);
              },
            }}
          />
        );
      case 'SuperSelect':
        let selectOption = superSelectProps?.options;
        if (!selectOption && enumCode && enumData) {
          selectOption = enumData[enumCode] ? enumData[enumCode].items || [] : [];
        }
        return (
          <SearchForm.SuperSelect
            {...restProps}
            {...baseFormProps}
            superSelectProps={{
              ...baseProps,
              ...superSelectProps,
              options: selectOption,
              onChange: (newValue, options) => {
                handleChange(newValue, options);
                superSelectProps?.onChange?.(newValue, options);
              },
            }}
          />
        );
      case 'SuperTransfer':
        return (
          <SearchForm.SuperTransfer
            {...restProps}
            {...baseFormProps}
            superTransferProps={{
              ...baseProps,
              ...superTransferProps,
              beforeFetch: params => {
                if (!superTransferProps) return params;
                if (!superTransferProps.beforeFetch) return params;
                return superTransferProps.beforeFetch?.(params, newGroupValue);
              },
              onChange: (newValue) => {
                handleChange(newValue,);
                superTransferProps?.onChange?.(newValue, form);
              },
            }}
          />
        );
      case 'SelectTab':
        return (
          <SearchForm.SelectTab
            {...restProps}
            {...baseFormProps}
            selectTabProps={{
              ...baseProps,
              ...selectTabProps,
              onChange: (newValue, options) => {
                handleChange(newValue, options);
                selectTabProps?.onChange?.(newValue, options);
              },
            }}
          />
        );
      case 'SuperSelectTable':
        if (!superSelectTableProps) return null;
        const { labelKey: labelFieldname, valueKey: valueFieldname, initialValue, ...restFormProps } = restProps;
        const initOptions = superSelectTableProps.initOptions || [];
        if (labelFieldname && valueFieldname && initialValue) {
          /** 下拉选项label对应字段 */
          const labelKey = superSelectTableProps.labelKey || superSelectTableProps.fieldNames?.label || DEFAULT_FIELD_NAMES.label;
          /** 下拉选项value对应字段 */
          const valueKey = superSelectTableProps.valueKey || superSelectTableProps.fieldNames?.value || DEFAULT_FIELD_NAMES.value;
          /** label对应的传入数据默认值 */
          const labelItem = groupValue[labelFieldname];
          /** value对应的传入数据默认值 */
          const valuelItem = groupValue[valueFieldname];
          if (Array.isArray(labelItem) && Array.isArray(valuelItem)) {
            valuelItem.forEach((item, index) => {
              const findItem = initOptions.find(v => v[valueKey] === item);
              if (!findItem) {
                initOptions.push({
                  [valueKey]: item,
                  [labelKey]: labelItem[index],
                });
              }
            });
          } else {
            if (labelItem && valuelItem) {
              const findItem = initOptions.find(v => v[valueKey] === valuelItem);
              if (!findItem) {
                initOptions.push({
                  [valueKey]: valuelItem,
                  [labelKey]: labelItem,
                });
              }
            }
          }
        }
        return (
          <SearchForm.SuperSelectTable
            {...restFormProps}
            {...baseFormProps}
            initialValue={initialValue}
            name={[baseFormProps.name]}
            superSelectTableProps={{
              ...baseProps,
              ...superSelectTableProps,
              initOptions: initOptions,
              beforeFetch: params => {
                if (!superSelectTableProps.beforeFetch) return params;
                return superSelectTableProps.beforeFetch?.(params, newGroupValue);
              },
              onChange: (newValue, options) => {
                handleChange(newValue, options);
                superSelectTableProps?.onChange?.(newValue, options, form, groupKey, newGroupValue);
              },
            }}
          />
        );
      default:
        return null;
    }
  };
  return renderForm();
};

export default CardFormItem;